import { useEffect } from "react";

import { jwtDecode } from "../../utils/utils";
import { isObject } from "underscore";
import { BLAdmin, type BrandliveEvent } from "../../types/working-model";
import { getLogger } from "../../utils/debug-logger";
import ALLOWED_ORIGINS from "./allowed-message-origins";

const logger = getLogger('bl-embed:message-listener');

export const GR_URLS: string[] = ['http://localhost:3000', process.env.REACT_APP_GREENROOM_FRONTEND_URL as string];

interface IProps {
	setAdminUser?: React.Dispatch<React.SetStateAction<BLAdmin | null>>;
	setToken?: React.Dispatch<React.SetStateAction<string | null>>;
	setHideFeed?: React.Dispatch<React.SetStateAction<boolean>>;
	onWorkingEvent?: (e: BrandliveEvent, fallbackVideo?: string) => void;
	onSuppressWatermark?: () => void;
}

export const useMessageListener = ({ setAdminUser, setToken, setHideFeed, onWorkingEvent, onSuppressWatermark }: IProps) => {
	useEffect(() => {
		const handleMessage = (e: MessageEvent) => {
			let url;
			try {
				url = new URL(e.origin);
			} catch (e) {
				console.error('Message without valid origin url', e);
				return;
			}

			// can get messages from browser extensions and we just want to ignore them rather than 
			// throw an error 
			if (ALLOWED_ORIGINS.has(url.origin)) {
				logger('Received message from parent at valid origin', e.data);

				if (e.data?.workingEvent) {
					onWorkingEvent?.(e.data.workingEvent as BrandliveEvent, e.data.fallbackVideo as string);
					return;
				}

				if (e?.data?.payload?.blToken) {
					const decodedUser = jwtDecode(e.data.payload.blToken);
					if (decodedUser && isObject(decodedUser)) {
						setAdminUser?.(decodedUser as BLAdmin);
					}
					setToken?.(e.data.payload.blToken);
				}

				if (e?.data?.payload?.hideFeed) {
					setHideFeed?.(true);
				}

				if (e?.data?.payload?.style && typeof e.data.payload.style === 'string') {
					document.head.insertAdjacentHTML("beforeend", `<style>${e.data.payload.style}</style>`);
				}

				if (e?.data?.suppressWatermark) {
					onSuppressWatermark?.();
				}
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [setAdminUser, setHideFeed, setToken, onWorkingEvent, onSuppressWatermark]);
};
